import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BriefcaseBusiness,
  Clock3,
  Eye,
  HelpCircle,
  MessageCircle,
  RefreshCw,
  Sparkles,
  Target,
  TrendingUp,
  UserRound,
  UsersRound,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { BackButton } from "../../../components/button/back-button";
import { useToast } from "../../../contexts/toast/toast-context";
import api from "../../../services/api";
import { formatUserPhone, getApiErrorMessage } from "../../../utils";
import type { CommercialStatus, User } from "../../../types/user";

type FunnelStatus = Exclude<CommercialStatus, "UNQUALIFIED">;
type FunnelContact = Pick<
  User,
  | "id"
  | "userName"
  | "userPhone"
  | "countryCode"
  | "dialCode"
  | "email"
  | "commercialStatus"
  | "updatedAt"
> & {
  commercialReason?: string;
  commercialClassifiedAt?: string;
  lastContactAt?: string;
  source: string;
  responsibleName?: string;
};
type FunnelStage = {
  status: FunnelStatus;
  total: number;
  contacts: FunnelContact[];
};
type FunnelResponse = { limit: number; stages: FunnelStage[] };

const STAGE_META: Record<
  FunnelStatus,
  {
    label: string;
    description: string;
    accent: string;
    emptyMessage: string;
  }
> = {
  LEAD: {
    label: "Lead",
    description:
      "Contato que demonstrou interesse em conhecer, avaliar ou obter informações sobre um produto ou serviço.",
    accent: "bg-violet-500",
    emptyMessage: "Novos contatos com interesse aparecerão aqui.",
  },
  QUALIFIED_LEAD: {
    label: "Lead qualificado",
    description:
      "Contato que confirmou uma necessidade e atende aos critérios mínimos de qualificação definidos pela empresa.",
    accent: "bg-amber-500",
    emptyMessage: "Leads que atenderem aos critérios avançarão para cá.",
  },
  OPPORTUNITY: {
    label: "Oportunidade",
    description:
      "Contato que manifestou intenção concreta de comprar, contratar ou avançar uma negociação.",
    accent: "bg-emerald-500",
    emptyMessage: "Contatos prontos para negociar aparecerão aqui.",
  },
};
const STAGES: FunnelStatus[] = ["LEAD", "QUALIFIED_LEAD", "OPPORTUNITY"];
const SOURCE_LABELS: Record<string, string> = {
  whatsapp: "WhatsApp",
  instagram: "Instagram",
  web: "Site",
  manual: "Cadastro manual",
};

function formatSource(source: string) {
  const normalized = source.trim().toLowerCase();
  return (
    SOURCE_LABELS[normalized] ||
    normalized.replace(/^./, (character) => character.toUpperCase())
  );
}

function formatLastContact(value?: string) {
  if (!value) return "Sem contato registrado";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sem contato registrado";
  const diffMinutes = Math.max(
    0,
    Math.floor((Date.now() - date.getTime()) / 60000),
  );
  if (diffMinutes < 1) return "Agora";
  if (diffMinutes < 60) return `Há ${diffMinutes} min`;
  if (diffMinutes < 1440) return `Há ${Math.floor(diffMinutes / 60)} h`;
  if (diffMinutes < 10080) return `Há ${Math.floor(diffMinutes / 1440)} d`;
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "short",
    year:
      date.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
  }).format(date);
}

export default function CommercialFunnelPage() {
  const navigate = useNavigate();
  const { error: showError, success } = useToast();
  const [data, setData] = useState<FunnelStage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [draggedContactId, setDraggedContactId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<FunnelStatus | null>(null);
  const [updatingContactIds, setUpdatingContactIds] = useState<Set<string>>(
    new Set(),
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get<FunnelResponse>("/commercial/funnel", {
        params: { limit: 50 },
      });
      setData(response.data.stages);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const moveContact = useCallback(
    async (contactId: string, targetStatus: FunnelStatus) => {
      const sourceStage = data.find((stage) =>
        stage.contacts.some((contact) => contact.id === contactId),
      );
      const contact = sourceStage?.contacts.find(
        (item) => item.id === contactId,
      );

      if (
        !sourceStage ||
        !contact ||
        sourceStage.status === targetStatus ||
        updatingContactIds.has(contactId)
      ) {
        return;
      }

      setUpdatingContactIds((current) => new Set(current).add(contactId));
      setData((current) =>
        STAGES.map((status) => {
          const stage = current.find((item) => item.status === status) ?? {
            status,
            total: 0,
            contacts: [],
          };

          if (status === sourceStage.status) {
            return {
              ...stage,
              total: Math.max(0, stage.total - 1),
              contacts: stage.contacts.filter((item) => item.id !== contactId),
            };
          }

          if (status === targetStatus) {
            return {
              ...stage,
              total: stage.total + 1,
              contacts: [
                {
                  ...contact,
                  commercialStatus: targetStatus,
                  commercialReason: undefined,
                  commercialClassifiedAt: undefined,
                },
                ...stage.contacts,
              ],
            };
          }

          return stage;
        }),
      );

      try {
        await api.patch(`/user/${contactId}`, {
          commercialStatus: targetStatus,
        });
        success(
          `${contact.userName} movido para ${STAGE_META[targetStatus].label}.`,
        );
      } catch (requestError) {
        showError(getApiErrorMessage(requestError));
        await load();
      } finally {
        setUpdatingContactIds((current) => {
          const next = new Set(current);
          next.delete(contactId);
          return next;
        });
      }
    },
    [data, load, showError, success, updatingContactIds],
  );

  const totals = useMemo(
    () =>
      Object.fromEntries(
        STAGES.map((status) => [
          status,
          data.find((stage) => stage.status === status)?.total ?? 0,
        ]),
      ) as Record<FunnelStatus, number>,
    [data],
  );
  const metrics = [
    { label: "Leads ativos", value: totals.LEAD, icon: UsersRound },
    { label: "Qualificados", value: totals.QUALIFIED_LEAD, icon: Target },
    { label: "Oportunidades", value: totals.OPPORTUNITY, icon: TrendingUp },
    {
      label: "Em evolução",
      value: totals.QUALIFIED_LEAD + totals.OPPORTUNITY,
      icon: Sparkles,
    },
  ];

  return (
    <div className="app-page">
      <BackButton />
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <h1 className="app-page-title">Funil de vendas</h1>
          <p className="mt-1 text-sm text-slate-500">
            Arraste os contatos entre as colunas para atualizar a classificação.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-neutral shrink-0"
          onClick={load}
          disabled={loading}
          aria-label="Atualizar funil de vendas"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          <span className="hidden sm:inline">Atualizar</span>
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <p>{error}</p>
          <button type="button" className="btn-link-danger mt-2" onClick={load}>
            Tentar novamente
          </button>
        </div>
      )}

      <section
        className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4"
        aria-label="Indicadores do funil"
      >
        {metrics.map(({ label, value, icon: Icon }) => (
          <article key={label} className="app-card p-4 sm:p-5">
            <div className="flex items-center justify-between gap-2 text-slate-500">
              <span className="text-xs font-medium sm:text-sm">{label}</span>
              <Icon className="h-4 w-4" aria-hidden="true" />
            </div>
            <strong className="mt-2 block text-2xl font-semibold text-slate-900">
              {loading ? "—" : value}
            </strong>
          </article>
        ))}
      </section>

      {loading ? (
        <div
          className="grid grid-cols-1 gap-4 lg:grid-cols-3"
          aria-label="Carregando funil"
        >
          {STAGES.map((status) => (
            <div
              key={status}
              className="app-card min-h-[32rem] animate-pulse bg-slate-100"
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-3">
          {STAGES.map((status) => {
            const stage = data.find((item) => item.status === status) ?? {
              status,
              total: 0,
              contacts: [],
            };
            const meta = STAGE_META[status];
            return (
              <section
                key={status}
                className={`app-card min-h-[32rem] min-w-0 overflow-hidden p-0 transition-colors ${
                  dropTarget === status
                    ? "ring-2 ring-violet-400 ring-offset-2"
                    : ""
                }`}
                onDragEnter={(event) => {
                  event.preventDefault();
                  setDropTarget(status);
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "move";
                }}
                onDragLeave={(event) => {
                  if (
                    !event.currentTarget.contains(event.relatedTarget as Node)
                  ) {
                    setDropTarget(null);
                  }
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  const contactId =
                    event.dataTransfer.getData("text/plain") ||
                    draggedContactId;
                  setDropTarget(null);
                  setDraggedContactId(null);
                  if (contactId) void moveContact(contactId, status);
                }}
              >
                <div
                  className={`h-1 w-full ${meta.accent}`}
                  aria-hidden="true"
                />
                <div className="p-4">
                  <header className="mb-4 flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-1.5">
                      <h2 className="truncate font-semibold text-slate-900">
                        {meta.label}
                      </h2>
                      <div className="group relative shrink-0">
                        <button
                          type="button"
                          className="flex rounded-full text-slate-400 transition hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
                          aria-label={`O que significa ${meta.label}`}
                          aria-describedby={`stage-description-${status}`}
                        >
                          <HelpCircle className="h-4 w-4" aria-hidden="true" />
                        </button>
                        <div
                          id={`stage-description-${status}`}
                          role="tooltip"
                          className="pointer-events-none absolute left-0 top-full z-20 mt-2 w-64 rounded-md bg-slate-900 px-3 py-2 text-xs font-normal leading-5 text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
                        >
                          {meta.description}
                        </div>
                      </div>
                    </div>
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
                      {stage.total}
                    </span>
                  </header>
                  <div className="space-y-3">
                    {stage.contacts.length === 0 && (
                      <div className="flex min-h-64 flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50/60 p-6 text-center">
                        <BriefcaseBusiness className="mb-3 h-8 w-8 text-slate-300" />
                        <p className="text-sm font-medium text-slate-600">
                          Nenhum contato neste estágio
                        </p>
                        <p className="mt-1 max-w-56 text-xs leading-5 text-slate-500">
                          {meta.emptyMessage}
                        </p>
                      </div>
                    )}
                    {stage.contacts.map((contact) => (
                      <article
                        key={contact.id}
                        className={`rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition hover:shadow-md ${
                          draggedContactId === contact.id ? "opacity-50" : ""
                        } ${
                          updatingContactIds.has(contact.id)
                            ? "cursor-wait opacity-70"
                            : "cursor-grab active:cursor-grabbing"
                        }`}
                        draggable={!updatingContactIds.has(contact.id)}
                        onDragStart={(event) => {
                          event.dataTransfer.effectAllowed = "move";
                          event.dataTransfer.setData("text/plain", contact.id);
                          setDraggedContactId(contact.id);
                        }}
                        onDragEnd={() => {
                          setDraggedContactId(null);
                          setDropTarget(null);
                        }}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <h3 className="truncate text-sm font-semibold text-slate-900">
                              {contact.userName}
                            </h3>
                            <p className="mt-1 truncate text-xs text-slate-500">
                              {formatUserPhone(contact) ||
                                "Telefone não informado"}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="btn-link-primary shrink-0 rounded p-1"
                            aria-label={`Visualizar contato ${contact.userName}`}
                            title="Visualizar contato"
                            onClick={() =>
                              navigate(
                                `/painel/contatos/visualizar/${contact.id}`,
                              )
                            }
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </div>
                        {contact.commercialReason && (
                          <div className="mt-3 flex items-start gap-2 rounded-md bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-600">
                            <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-violet-500" />
                            <span>{contact.commercialReason}</span>
                          </div>
                        )}
                        <dl className="mt-3 space-y-2 border-t border-slate-100 pt-3 text-xs text-slate-500">
                          <div className="flex items-center justify-between gap-3">
                            <dt className="flex items-center gap-1.5">
                              <MessageCircle className="h-3.5 w-3.5" /> Origem
                            </dt>
                            <dd className="truncate font-medium text-slate-700">
                              {formatSource(contact.source)}
                            </dd>
                          </div>
                          <div className="flex items-center justify-between gap-3">
                            <dt className="flex items-center gap-1.5">
                              <UserRound className="h-3.5 w-3.5" /> Responsável
                            </dt>
                            <dd className="truncate font-medium text-slate-700">
                              {contact.responsibleName || "Não atribuído"}
                            </dd>
                          </div>
                          <div className="flex items-center justify-between gap-3">
                            <dt className="flex items-center gap-1.5">
                              <Clock3 className="h-3.5 w-3.5" /> Último contato
                            </dt>
                            <dd className="font-medium text-slate-700">
                              {formatLastContact(contact.lastContactAt)}
                            </dd>
                          </div>
                        </dl>
                      </article>
                    ))}
                  </div>
                  {stage.total > stage.contacts.length && (
                    <p className="mt-4 text-center text-xs text-slate-500">
                      Exibindo {stage.contacts.length} de {stage.total}{" "}
                      contatos.
                    </p>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
